import {
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBuildingBank,
  IconCar,
  IconCash,
  IconClipboardCheck,
  IconClock,
  IconId,
  IconIdBadge,
  IconMail,
  IconRefresh,
} from '@tabler/icons-react';
import { HighwayMaintenanceFeeStateBadge } from './HighwayMaintenanceFeeStateBadge';
import {
  HighwayMaintenanceFeeTicketState,
  HighwayMaintenanceFeeTicketSummary,
} from '../model';
import { CopyText } from '#/components/ui/tickets/components/CopyText.tsx';
import {
  TICKET_TYPE,
  TICKET_TYPE_CONFIG,
} from '#/components/ui/tickets/config';
import { formatDateTime } from '#/utils/DateUtil';

type CardFieldRowProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

function CardFieldRow({ icon, label, value }: CardFieldRowProps) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text c="dimmed" size="sm" w={16} style={{ flexShrink: 0 }}>
        {icon}
      </Text>
      <Text size="sm" c="dimmed" w={70} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Group gap={4} style={{ flex: 1, justifyContent: 'flex-end' }}>
        {value}
      </Group>
    </Group>
  );
}

const CONFIG = TICKET_TYPE_CONFIG[TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_TICKETS];

type CardProps = HighwayMaintenanceFeeTicketSummary & {
  onDetailClick: () => void;
  onConfirmPaymentClick: () => void;
  onFillQueryResultClick: () => void;
};

export function HighwayMaintenanceFeeCard({
  account,
  plateNumber,
  idNumber,
  taxIdNumber,
  state,
  createDateTime,
  updateDateTime,
  onDetailClick,
  onConfirmPaymentClick,
  onFillQueryResultClick,
}: CardProps) {
  return (
    <Paper withBorder p="md" radius="md" shadow="none">
      <Stack gap="sm">
        {/* header */}
        <Group justify="space-between">
          <Text fw={600}>{CONFIG.name}</Text>
          <HighwayMaintenanceFeeStateBadge state={state} />
        </Group>

        <Divider />

        {/* fields */}
        <Stack gap={6}>
          <CardFieldRow
            icon={<IconMail size={14} />}
            label="Email"
            value={
              <Tooltip label={account.email}>
                <Text size="sm" lineClamp={1} className="break-all">
                  {account.email}
                </Text>
              </Tooltip>
            }
          />
          <CardFieldRow
            icon={<IconCar size={14} />}
            label="車牌"
            value={<CopyText size="sm">{plateNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconIdBadge size={14} />}
            label="證件號碼"
            value={<CopyText size="sm">{idNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconId size={14} />}
            label="統一編號"
            value={<CopyText size="sm">{taxIdNumber ?? '—'}</CopyText>}
          />
          <CardFieldRow
            icon={<IconBuildingBank size={14} />}
            label="查繳平台"
            value={<Text size="sm">{CONFIG.queryPlatform}</Text>}
          />
        </Stack>

        <Divider />

        {/* timestamps */}
        <Stack gap={6}>
          <CardFieldRow
            icon={<IconClock size={14} />}
            label="建立時間"
            value={
              <Text size="sm">
                {createDateTime ? formatDateTime(createDateTime) : '—'}
              </Text>
            }
          />
          <CardFieldRow
            icon={<IconRefresh size={14} />}
            label="最後更新"
            value={
              <Text size="sm">
                {updateDateTime ? formatDateTime(updateDateTime) : '—'}
              </Text>
            }
          />
        </Stack>

        <Divider />

        {/* actions */}
        <Group gap="xs">
          {state === HighwayMaintenanceFeeTicketState.CHARGE_SUCCESS && (
            <Button
              flex={'100 0 auto'}
              variant="light"
              size="sm"
              leftSection={<IconClipboardCheck size={14} />}
              onClick={onConfirmPaymentClick}
            >
              確認已代繳
            </Button>
          )}
          {state === HighwayMaintenanceFeeTicketState.PENDING && (
            <Button
              flex={'100 0 auto'}
              variant="light"
              size="sm"
              leftSection={<IconCash size={14} />}
              onClick={onFillQueryResultClick}
            >
              回填查詢結果
            </Button>
          )}
          <Button
            flex={'1 1 auto'}
            variant="outline"
            size="sm"
            onClick={onDetailClick}
          >
            查看詳情
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
